/**
 * Hyperliquid HIP-4 outcome markets constants.
 *
 * Two encoding forms for the same asset (do NOT mix them up):
 *   - Full asset id (used in `order.a` action):     100_000_000 + 10*outcome + side
 *   - Coin syntax (used in info `coin` field):       "#" + (10*outcome + side)
 *
 * Spot markets use the different `@<index>` prefix. The smoke test
 * (`scripts/smoke-hyperliquid.ts`) confirmed both forms live on mainnet.
 */
/**
 * Set HYPERLIQUID_TESTNET=1 to switch the entire HL stack to Arbitrum
 * Sepolia + the testnet API. The signing chain id, Bridge2 address, and
 * USDC token address all flip together — no individual override needed.
 * Faucet for free test USDC: https://app.hyperliquid-testnet.xyz/drip
 */
export const HYPERLIQUID_IS_TESTNET = process.env.HYPERLIQUID_TESTNET === '1'

export const HYPERLIQUID_INFO_URL =
	process.env.HYPERLIQUID_API_BASE ||
	(HYPERLIQUID_IS_TESTNET ? 'https://api.hyperliquid-testnet.xyz' : 'https://api.hyperliquid.xyz')
export const HYPERLIQUID_WS_URL =
	process.env.HYPERLIQUID_WS_URL ||
	(HYPERLIQUID_IS_TESTNET ? 'wss://api.hyperliquid-testnet.xyz/ws' : 'wss://api.hyperliquid.xyz/ws')

// HL Bridge2 contract receives plain USDC ERC20 transfers and validators
// credit the corresponding HyperCore account. Address differs per env —
// don't send mainnet USDC to the testnet Bridge2 or vice versa.
export const HYPERLIQUID_BRIDGE2_ARBITRUM = HYPERLIQUID_IS_TESTNET
	? '0x08cfc1B6b2dCF36A1480b99353A354AA8AC56f89' // Arbitrum Sepolia
	: '0x2df1c51e09aecf9cacb7bc98cb1742757f163df7' // Arbitrum One

// USDC token address user sends to Bridge2. Mainnet is Circle's native
// USDC; testnet is the Hyperliquid-deployed mock USDC on Arbitrum Sepolia.
export const HYPERLIQUID_ARB_USDC = HYPERLIQUID_IS_TESTNET
	? '0x1baAbB04529D43a73232B713C0FE471f7c7334d5' // Arbitrum Sepolia mock USDC
	: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831' // Arbitrum One native USDC

// SponsoredChain key for @/lib/wallet-core/sponsored — selects which
// Alchemy infra chain config + paymaster policy to use for Bridge2
// deposits and post-withdraw Relay legs.
export const HYPERLIQUID_SPONSORED_CHAIN: 'arbitrum' | 'arbitrumSepolia' = HYPERLIQUID_IS_TESTNET
	? 'arbitrumSepolia'
	: 'arbitrum'

export const HYPERLIQUID_MIN_DEPOSIT_USDC = 5

// USDH/USDC spot pair index used by stablecoin conversion on @230.
// Used for legacy USDH→USDC migration. Index differs per env —
// mainnet @230, testnet @1338. Asset id = 10_000 + pair_index.
export const HYPERLIQUID_USDH_USDC_PAIR_INDEX = HYPERLIQUID_IS_TESTNET ? 1338 : 230
// HL rejects any order whose notional value (price * size) is below 10 USDH.
// Empirically confirmed from /exchange: "Order must have minimum value of 10
// USDH." HIP-4 outcomes share the same minimum as perps.
export const HYPERLIQUID_MIN_ORDER_NOTIONAL_USD = 10

export const OUTCOME_ASSET_OFFSET = 100_000_000

export function encodeOutcomeAssetId(outcome: number, side: 0 | 1): number {
	return OUTCOME_ASSET_OFFSET + 10 * outcome + side
}

export function encodeOutcomeCoin(outcome: number, side: 0 | 1): `#${number}` {
	return `#${10 * outcome + side}` as `#${number}`
}

export function decodeOutcomeAssetId(assetId: number): { outcome: number; side: 0 | 1 } | null {
	if (assetId < OUTCOME_ASSET_OFFSET) return null
	const enc = assetId - OUTCOME_ASSET_OFFSET
	return { outcome: Math.floor(enc / 10), side: (enc % 10) as 0 | 1 }
}

// Base-tier spot fee schedule from HL /info userFees → feeSchedule.
// HIP-4 outcomes trade on the spot engine, so spotCross/spotAdd apply.
// VIP tiers lower these, but showing base tier is the safe default.
export const HL_SPOT_TAKER_FEE = 0.0007
export const HL_SPOT_MAKER_FEE = 0.0004
